import type { TooltipProps } from "@mui/material";
import { Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import DynamicTruncateInside from "./DynamicTruncateInside";

interface TruncatedTextWithTooltipProps {
  text: string;
  // By default, tooltip will show the full `text` if it is truncated - if `tooltipText` is provided, it will use that instead
  tooltipText?: string;
  placement?: TooltipProps["placement"];
}

const TruncatedTextWithTooltip: React.FC<TruncatedTextWithTooltipProps> = ({
  text,
  tooltipText,
  placement = "bottom",
}) => {
  // Use Unicode "one dot leader" because it looks better than regular "..."
  // and allows us to detect it as a special character to know if the text is truncated
  const ellipsis = "․․․";

  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const displayTooltip = tooltipText || text;

  useEffect(() => {
    const checkForTruncation = () => {
      if (contentRef.current) {
        // Use textContent, not innerText: the rendered text is briefly kept
        // visibility:hidden while its truncation settles (see DynamicTruncateInside),
        // and innerText returns "" for hidden content, which would hide the tooltip.
        const contentText = contentRef.current.textContent || "";
        const isContentTruncated = contentText.includes(ellipsis);
        setIsTruncated(isContentTruncated);
      }
    };

    checkForTruncation();

    const observer = new MutationObserver(checkForTruncation);
    if (contentRef.current) {
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => observer.disconnect();
  }, [text]);

  const content = (
    <div ref={contentRef}>
      <DynamicTruncateInside ellipsis={ellipsis} text={text} />
    </div>
  );

  const shouldShowTooltip = isTruncated || !!tooltipText;

  // Always render the Tooltip but hide it when not needed - this prevents re-renders that can cause flickering
  return (
    <Tooltip
      title={displayTooltip}
      placement={placement}
      disableInteractive
      disableHoverListener={!shouldShowTooltip}
    >
      <div style={{ overflow: "hidden" }}>
        <div style={{ paddingRight: "4px" }}>{content}</div>
      </div>
    </Tooltip>
  );
};

export default TruncatedTextWithTooltip;
